import React, { FunctionComponent, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, ButtonBase, Container, Stack, Typography } from '@mui/material';
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import Lottie from 'lottie-react';

import { useAppSelector } from '../../../../hooks';
import PaymentProcessingAnimation from '../../../../lottieAnimations/payment-processing.json';
import {
  getCourseSubscriptionSession,
  getStripeV2State,
  resetCourseSubscriptionSession,
} from '../../../../store/V2/StripeSlice';

const InvoiceProcessingPage: FunctionComponent = () => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Store
  const stripeState = useAppSelector(getStripeV2State);

  // State
  const [retryCount, setRetryCount] = useState(0);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | undefined>(
    undefined
  );

  const questyStringParams = new URLSearchParams(location.search);
  const isCanceled = questyStringParams.get('paymentStatus') === 'canceled';
  const { stripeSessionId } = params;
  const { courseSubscriptionSession } = stripeState;

  useEffect(() => {
    if (!isCanceled) {
      const interval = setInterval(() => {
        if (stripeSessionId) {
          dispatch(getCourseSubscriptionSession(stripeSessionId));
          setRetryCount((prevVal) => prevVal + 1);
        }
      }, 5000);

      setIntervalId(interval);
    }

    return () => {
      clearInterval(intervalId);
      dispatch(resetCourseSubscriptionSession());
    };
  }, []);

  useEffect(() => {
    if (
      courseSubscriptionSession &&
      courseSubscriptionSession.status === 'PAID'
    ) {
      clearInterval(intervalId);
    }
  }, [courseSubscriptionSession]);

  useEffect(() => {
    if (retryCount > 4) {
      clearInterval(intervalId);
    }
  }, [retryCount]);

  if (isCanceled) {
    return (
      <Container maxWidth="xl">
        <Stack padding="30px" textAlign="center" gap="20px" alignItems="center">
          <ErrorIcon sx={{ fontSize: '80px', color: 'error.main' }} />
          <Typography variant="h4">Payment Cancelled</Typography>
          <Typography variant="h6">
            <ButtonBase
              onClick={() => navigate(`${process.env.PUBLIC_URL}/portal`)}
              sx={{
                fontSize: 'inherit',
                fontWeight: 'inherit',
                color: 'primary.main',
              }}
            >
              <b>Go Home</b>
            </ButtonBase>
          </Typography>
        </Stack>
      </Container>
    );
  }

  if (
    courseSubscriptionSession &&
    courseSubscriptionSession.status === 'PAID'
  ) {
    return (
      <Container maxWidth="xl">
        <Stack padding="30px" textAlign="center" gap="20px" alignItems="center">
          <CheckCircleOutlineIcon
            sx={{ fontSize: '80px', color: 'primary.main' }}
          />
          <Typography variant="h4">Payment Successful</Typography>
          <Typography variant="h6">
            Thank you for the payment.&nbsp;
            <ButtonBase
              onClick={() => navigate(`${process.env.PUBLIC_URL}/portal`)}
              sx={{
                fontSize: 'inherit',
                fontWeight: 'inherit',
                color: 'primary.main',
              }}
            >
              <b>Go Home</b>
            </ButtonBase>
          </Typography>
        </Stack>
      </Container>
    );
  }

  if (retryCount > 4 && courseSubscriptionSession) {
    return (
      <Container maxWidth="xl">
        <Stack padding="30px" textAlign="center" gap="20px" alignItems="center">
          <ErrorIcon sx={{ fontSize: '80px', color: 'error.main' }} />
          <Typography variant="h4">Payment Failed</Typography>
          <Typography variant="h6">
            Please contact the support team.&nbsp;
            <ButtonBase
              onClick={() => navigate(`${process.env.PUBLIC_URL}/portal`)}
              sx={{
                fontSize: 'inherit',
                fontWeight: 'inherit',
                color: 'primary.main',
              }}
            >
              <b>Go Home</b>
            </ButtonBase>
          </Typography>
        </Stack>
      </Container>
    );
  }

  return (
    <Stack
      direction="row"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      style={{
        height: '70vh',
        padding: '0 15px',
      }}
    >
      <Lottie
        animationData={PaymentProcessingAnimation}
        loop
        style={{ width: '400px', maxWidth: '100%' }}
      />
      <div>
        <Typography variant="h6">Payment Processing</Typography>
      </div>
      <div>
        <Typography color="secondary">
          We&apos;re processing your credit card. This process may take a few
          <Box component="br" sx={{ display: { xs: 'none', sm: 'block' } }} />
          seconds, So please be patient.
        </Typography>
      </div>
    </Stack>
  );
};

export default InvoiceProcessingPage;
